import axios from 'axios';
import { 
  InsuranceCarrier,
  InsuranceProvider, 
  InsuranceClaim, 
  InsurancePolicy, 
  InsuranceVerification,
  PaginatedResponse 
} from '../types';

const API_BASE_URL = '/api/insurance';

// Insurance Carrier services
export const insuranceCarrierService = {
  // Get all carriers
  getAll: async (params?: { page?: number; limit?: number; isActive?: boolean }): Promise<PaginatedResponse<InsuranceCarrier>> => {
    const response = await axios.get(`${API_BASE_URL}/carriers`, { params });
    return response.data;
  },

  // Get carrier by ID
  getById: async (id: string): Promise<InsuranceCarrier> => {
    const response = await axios.get(`${API_BASE_URL}/carriers/${id}`);
    return response.data.data;
  },

  // Get carriers by waiting period range
  getByWaitingPeriod: async (minDays?: number, maxDays?: number): Promise<{ success: boolean; data: InsuranceCarrier[]; count: number }> => {
    const params: any = {};
    if (minDays !== undefined) params.minDays = minDays;
    if (maxDays !== undefined) params.maxDays = maxDays;
    
    const response = await axios.get(`${API_BASE_URL}/carriers/waiting-period`, { params });
    return response.data;
  },

  // Create new carrier
  create: async (carrier: Omit<InsuranceCarrier, '_id' | 'createdAt' | 'updatedAt'>): Promise<InsuranceCarrier> => {
    const response = await axios.post(`${API_BASE_URL}/carriers`, carrier);
    return response.data.data;
  },

  // Update carrier
  update: async (id: string, carrier: Partial<InsuranceCarrier>): Promise<InsuranceCarrier> => {
    const response = await axios.put(`${API_BASE_URL}/carriers/${id}`, carrier);
    return response.data.data;
  },

  // Delete carrier
  delete: async (id: string): Promise<void> => {
    await axios.delete(`${API_BASE_URL}/carriers/${id}`);
  }
};

// Insurance Provider services
export const insuranceProviderService = {
  // Get all providers
  getAll: async (params?: { page?: number; limit?: number; type?: string; isActive?: boolean }): Promise<PaginatedResponse<InsuranceProvider>> => {
    const response = await axios.get(`${API_BASE_URL}/providers`, { params });
    return response.data;
  },

  // Get provider by ID
  getById: async (id: string): Promise<InsuranceProvider> => {
    const response = await axios.get(`${API_BASE_URL}/providers/${id}`);
    return response.data.data;
  },

  // Create new provider
  create: async (provider: Omit<InsuranceProvider, 'id' | 'createdAt' | 'updatedAt'>): Promise<InsuranceProvider> => {
    const response = await axios.post(`${API_BASE_URL}/providers`, provider);
    return response.data.data;
  },

  // Update provider
  update: async (id: string, provider: Partial<InsuranceProvider>): Promise<InsuranceProvider> => {
    const response = await axios.put(`${API_BASE_URL}/providers/${id}`, provider);
    return response.data.data;
  },

  // Delete provider
  delete: async (id: string): Promise<void> => {
    await axios.delete(`${API_BASE_URL}/providers/${id}`);
  }
};

// Insurance Claim services
export const insuranceClaimService = {
  // Get all claims
  getAll: async (params?: { page?: number; limit?: number; status?: string; patientId?: string }): Promise<PaginatedResponse<InsuranceClaim>> => {
    const response = await axios.get(`${API_BASE_URL}/claims`, { params });
    return response.data;
  },

  // Get claim by ID
  getById: async (id: string): Promise<InsuranceClaim> => {
    const response = await axios.get(`${API_BASE_URL}/claims/${id}`);
    return response.data.data;
  },

  // Create new claim
  create: async (claim: Omit<InsuranceClaim, 'id' | 'createdAt' | 'updatedAt'>): Promise<InsuranceClaim> => {
    const response = await axios.post(`${API_BASE_URL}/claims`, claim);
    return response.data.data;
  },

  // Update claim
  update: async (id: string, claim: Partial<InsuranceClaim>): Promise<InsuranceClaim> => {
    const response = await axios.put(`${API_BASE_URL}/claims/${id}`, claim);
    return response.data.data;
  },

  // Delete claim
  delete: async (id: string): Promise<void> => {
    await axios.delete(`${API_BASE_URL}/claims/${id}`);
  }
};

// Insurance Policy services
export const insurancePolicyService = {
  // Get all policies
  getAll: async (params?: { page?: number; limit?: number; isActive?: boolean; patientId?: string }): Promise<PaginatedResponse<InsurancePolicy>> => {
    const response = await axios.get(`${API_BASE_URL}/policies`, { params });
    return response.data;
  },

  // Get policy by ID
  getById: async (id: string): Promise<InsurancePolicy> => {
    const response = await axios.get(`${API_BASE_URL}/policies/${id}`);
    return response.data.data;
  },

  // Create new policy
  create: async (policy: Omit<InsurancePolicy, 'id' | 'createdAt' | 'updatedAt'>): Promise<InsurancePolicy> => {
    const response = await axios.post(`${API_BASE_URL}/policies`, policy);
    return response.data.data;
  },

  // Update policy
  update: async (id: string, policy: Partial<InsurancePolicy>): Promise<InsurancePolicy> => {
    const response = await axios.put(`${API_BASE_URL}/policies/${id}`, policy);
    return response.data.data;
  },

  // Delete policy
  delete: async (id: string): Promise<void> => {
    await axios.delete(`${API_BASE_URL}/policies/${id}`);
  }
};

// Insurance Verification services
export const insuranceVerificationService = {
  // Get all verifications
  getAll: async (params?: { page?: number; limit?: number; eligibilityStatus?: string; patientId?: string }): Promise<PaginatedResponse<InsuranceVerification>> => {
    const response = await axios.get(`${API_BASE_URL}/verifications`, { params });
    return response.data;
  },

  // Get verification by ID
  getById: async (id: string): Promise<InsuranceVerification> => {
    const response = await axios.get(`${API_BASE_URL}/verifications/${id}`);
    return response.data.data;
  },

  // Create new verification
  create: async (verification: Omit<InsuranceVerification, 'id' | 'createdAt' | 'updatedAt'>): Promise<InsuranceVerification> => {
    const response = await axios.post(`${API_BASE_URL}/verifications`, verification);
    return response.data.data;
  },

  // Update verification
  update: async (id: string, verification: Partial<InsuranceVerification>): Promise<InsuranceVerification> => {
    const response = await axios.put(`${API_BASE_URL}/verifications/${id}`, verification);
    return response.data.data;
  },

  // Delete verification
  delete: async (id: string): Promise<void> => {
    await axios.delete(`${API_BASE_URL}/verifications/${id}`);
  }
};
